import React, { useState } from 'react';

import { List } from '@buildoutinc/blueprint-react/ui/List';
import { Modal } from '@buildoutinc/blueprint-react/ui/Modal';

import { Alert } from 'components/ui/alert';
import { Button } from 'components/ui/button';
import { post } from 'helpers/api';
import { pluralize } from 'helpers/formatters/string_formats';

import type { PageRecord } from './types';

interface Props {
  collectionPagesUrl: string;
  companyId: number | null;
  page: PageRecord;
  onClose: () => void;
  onMoved: () => void;
}

// Joining the collection takes the page out of every document it's in, so the documents are listed first.
export default function MoveToMarketOverviewsModal({ collectionPagesUrl, companyId, page, onClose, onMoved }: Props) {
  const [isSaving, setIsSaving] = useState(false);
  const [hasFailed, setHasFailed] = useState(false);

  // One row per document: two documents can share a name.
  const documents = [...new Map(page.documents.map(usage => [usage.documentTemplateId, usage])).values()];

  const move = async () => {
    setIsSaving(true);
    setHasFailed(false);
    try {
      await post(collectionPagesUrl, { pageId: page.id, companyId: companyId ?? undefined });
      onMoved();
    } catch {
      setHasFailed(true);
      setIsSaving(false);
    }
  };

  return (
    <Modal open onClose={() => !isSaving && onClose()}>
      <Modal.Content centered>
        <Modal.Header>
          <Modal.Title>Move {page.name} to Market Overviews?</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>
            Market pages reach documents only through the Market Overview slot, which picks them by the
            property&apos;s market. Tag the page with a market once it&apos;s moved.
          </p>
          {documents.length > 0 ? (
            <>
              <p className="mb-2">
                It comes out of {pluralize(page.documentsCount, 'document')} it&apos;s in now:
              </p>
              <List>
                {documents.map(usage => (
                  <List.Item key={usage.documentTemplateId}>{usage.documentTemplateName}</List.Item>
                ))}
              </List>
            </>
          ) : (
            <p className="mb-0">It isn&apos;t in any documents now.</p>
          )}
          {hasFailed && (
            <Alert className="mt-3" severity="destructive">
              <Alert.Title>The page couldn&apos;t be moved. Try again.</Alert.Title>
            </Alert>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button disabled={isSaving} variant="ghost" onClick={onClose}>Cancel</Button>
          <Button disabled={isSaving} variant="primary" onClick={move}>Move to Market Overviews</Button>
        </Modal.Footer>
      </Modal.Content>
    </Modal>
  );
}
